import { Link } from "@tanstack/react-router";
import { Package, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ProductCreateDialog } from "@/components/products/ProductCreateDialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { type Product, useDeleteProduct, useProducts } from "@/hooks/use-products";
import { PRODUCT_STATUS_OPTIONS } from "@/lib/enums";
import { formatCurrency } from "@/lib/format";

export function ProductsTable() {
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);

  const { data, isLoading, isError, error, refetch } = useProducts(grid.query, grid.pageRequest);
  const deleteProduct = useDeleteProduct();

  const products = data?.data ?? [];
  const total = data?.total ?? 0;
  const hasNext = data?.hasNext ?? false;
  const hasPrev = data?.hasPrev ?? false;

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteProduct.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const toolbar = (
    <div className="flex items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search products..."
        className="flex-1 max-w-sm"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={PRODUCT_STATUS_OPTIONS}
        allOption={{ label: "All statuses" }}
        placeholder="All statuses"
        className="w-[160px]"
        aria-label="Filter by status"
      />
      <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
        <Plus className="h-4 w-4 mr-1" />
        New product
      </Button>
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? "Failed to load products"}
          onRetry={() => refetch()}
        />
        <ProductCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "Name", sortKey: "name" },
          { label: "Status", sortKey: "status" },
          { label: "SKU", className: "hidden md:table-cell", sortKey: "sku" },
          { label: "Price", className: "text-right", sortKey: "priceCents" },
          { label: "", className: "w-12" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Package}
            title="No products found"
            description="Try adjusting your search or filters."
          />
        }
      >
        {products.map((product) => (
          <tr
            key={product.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">
              <Link
                to="/dashboard/products/$productId"
                params={{ productId: product.id }}
                className="hover:text-primary transition-colors"
              >
                {product.name}
              </Link>
            </td>
            <td className="px-4 py-3">
              <StatusBadge status={product.status} />
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground font-mono text-xs">
              {product.sku}
            </td>
            <td className="px-4 py-3 text-right font-medium">
              {formatCurrency(product.priceCents)}
            </td>
            <td className="px-4 py-3 text-right">
              <RowActions
                actions={[
                  {
                    label: "Delete",
                    icon: Trash2,
                    destructive: true,
                    onSelect: () => setDeleteTarget(product),
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={total}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="products"
      />

      <ProductCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete product"
        description={`Delete ${deleteTarget?.name ?? "this product"}? Products referenced by existing orders cannot be deleted.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteProduct.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
